import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceQaService } from '../services/maintenance-qa.service';
import type { 
  QaInspection, 
  QaFilters 
} from '../types/maintenance-qa.types';
import { useState } from 'react';

export function useQa(companyId: string, initialFilters: QaFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<QaFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-qa', companyId, filters],
    queryFn: () => MaintenanceQaService.getInspections(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-qa-stats', companyId],
    queryFn: () => MaintenanceQaService.getQaStats(companyId),
    enabled: !!companyId,
  });

  const createInspectionMutation = useMutation({
    mutationFn: (data: Partial<QaInspection>) => MaintenanceQaService.createInspection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-qa'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-qa-stats'] });
    },
  });

  const submitResultMutation = useMutation({
    mutationFn: ({ id, score, result, notes }: { id: string, score: number, result: string, notes?: string }) => 
      MaintenanceQaService.submitResult(id, { score, result, notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-qa'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-qa', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    inspections: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createInspection: createInspectionMutation.mutateAsync,
    submitResult: submitResultMutation.mutateAsync,
    isCreating: createInspectionMutation.isPending,
    isSubmitting: submitResultMutation.isPending,
  };
}

export function useQaDetail(id?: string) {
  const detailQuery = useQuery({
    queryKey: ['maintenance-qa', 'detail', id],
    queryFn: () => MaintenanceQaService.getInspectionDetail(id!),
    enabled: !!id,
  });

  const itemsQuery = useQuery({
    queryKey: ['maintenance-qa', 'items', id],
    queryFn: () => MaintenanceQaService.getInspectionItems(id!),
    enabled: !!id,
  });

  return {
    ...detailQuery,
    inspection: detailQuery.data,
    items: itemsQuery.data || [],
    isLoadingItems: itemsQuery.isLoading,
  };
}
